import React, { useCallback } from 'react';
import { Table, Button, Input, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FT161FormItem, T206FormData } from '../hooks/useT206Record';

interface Props {
  attendees: FT161FormItem[];
  isEditing: boolean;
  setField: <K extends keyof T206FormData>(field: K, value: T206FormData[K]) => void;
}

const Tab2OurAttendees: React.FC<Props> = ({ attendees, isEditing, setField }) => {
  const handleChange = useCallback((idx: number, key: keyof FT161FormItem, value: string) => {
    const updated = attendees.map((row, i) =>
      i === idx ? { ...row, [key]: value } : row
    );
    setField('our_attendees', updated);
  }, [attendees, setField]);

  const handleAdd = useCallback(() => {
    setField('our_attendees', [
      ...attendees,
      { ft161_11: '', ft161_11a: '', ft161_11b: '', ft161_12: '', ft161_12a: '' },
    ]);
  }, [attendees, setField]);

  const handleDelete = useCallback((idx: number) => {
    setField('our_attendees', attendees.filter((_, i) => i !== idx));
  }, [attendees, setField]);

  const columns = [
    {
      title: '序',
      dataIndex: 'ft161_02',
      width: 40,
      render: (v: number, _: unknown, idx: number) => v ?? idx + 1,
    },
    {
      title: '員工代碼',
      dataIndex: 'ft161_11',
      width: 100,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={8}
            onChange={e => handleChange(idx, 'ft161_11', e.target.value)}
          />
        ) : v,
    },
    {
      title: '中文姓名',
      dataIndex: 'ft161_11a',
      width: 120,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={20}
            onChange={e => handleChange(idx, 'ft161_11a', e.target.value)}
          />
        ) : v,
    },
    {
      title: '英文名',
      dataIndex: 'ft161_11b',
      width: 160,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={40}
            onChange={e => handleChange(idx, 'ft161_11b', e.target.value)}
          />
        ) : v,
    },
    {
      title: '部門代碼',
      dataIndex: 'ft161_12',
      width: 90,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={4}
            onChange={e => handleChange(idx, 'ft161_12', e.target.value)}
          />
        ) : v,
    },
    {
      title: '部門名稱',
      dataIndex: 'ft161_12a',
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={20}
            onChange={e => handleChange(idx, 'ft161_12a', e.target.value)}
          />
        ) : v,
    },
    ...(isEditing
      ? [
          {
            title: '',
            width: 40,
            render: (_: unknown, __: unknown, idx: number) => (
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(idx)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={handleAdd}>
            新增人員
          </Button>
        </Space>
      )}
      <Table
        columns={columns}
        dataSource={attendees.map((row, i) => ({ ...row, key: i }))}
        size="small"
        pagination={false}
        scroll={{ x: 600 }}
        locale={{ emptyText: '尚無我司參加人員' }}
      />
    </div>
  );
};

export default Tab2OurAttendees;
